import React, { useState, useEffect } from "react";

const NPVCalculator = () => {
  const [initialInvestment, setInitialInvestment] = useState(500000);
  const [discountRate, setDiscountRate] = useState(10);
  const [year1CashFlow, setYear1CashFlow] = useState(150000);
  const [year2CashFlow, setYear2CashFlow] = useState(180000);
  const [year3CashFlow, setYear3CashFlow] = useState(200000);
  const [year4CashFlow, setYear4CashFlow] = useState(220000);
  const [year5CashFlow, setYear5CashFlow] = useState(250000);
  const [periodType, setPeriodType] = useState("annual");
  const [result, setResult] = useState({
    npv: "0.00",
    totalCashInflows: "0.00",
    presentValueInflows: "0.00",
    irr: "0.00",
    profitabilityIndex: "0.00",
    paybackPeriod: "0.00",
    investmentDecision: "",
    investmentRating: "",
  });

  // NPV Calculation Function
  const calculateNPV = () => {
    const initial = parseFloat(initialInvestment);
    const rate = parseFloat(discountRate) / 100;
    const cf1 = parseFloat(year1CashFlow);
    const cf2 = parseFloat(year2CashFlow);
    const cf3 = parseFloat(year3CashFlow);
    const cf4 = parseFloat(year4CashFlow);
    const cf5 = parseFloat(year5CashFlow);

    if (isNaN(initial) || isNaN(rate) || initial < 0 || rate < 0) return;

    const cashFlows = [cf1, cf2, cf3, cf4, cf5];
    const totalCashInflows = cashFlows.reduce((sum, cf) => sum + cf, 0);

    // Calculate Present Value of each cash flow
    let presentValueInflows = 0;
    const pvArray = [];
    
    for (let i = 0; i < cashFlows.length; i++) {
      const pv = cashFlows[i] / Math.pow(1 + rate, i + 1);
      presentValueInflows += pv;
      pvArray.push(pv);
    }

    // Calculate NPV
    const npv = presentValueInflows - initial;

    // Calculate IRR using Newton-Raphson method (simplified)
    let irr = 0.1; // Initial guess
    let iterations = 0;
    const maxIterations = 100;
    const tolerance = 0.0001;

    while (iterations < maxIterations) {
      let npvAtRate = -initial;
      let derivative = 0;
      
      for (let i = 0; i < cashFlows.length; i++) {
        npvAtRate += cashFlows[i] / Math.pow(1 + irr, i + 1);
        derivative -= (i + 1) * cashFlows[i] / Math.pow(1 + irr, i + 2);
      }
      
      if (Math.abs(npvAtRate) < tolerance) break;
      if (derivative === 0) break;
      
      irr = irr - npvAtRate / derivative;
      iterations++;
    }

    // If IRR calculation failed or is unrealistic, set to N/A
    const irrResult = (iterations < maxIterations && irr > -1 && irr < 10) ? irr * 100 : null;

    // Calculate Profitability Index
    const profitabilityIndex = presentValueInflows / initial;

    // Calculate Payback Period (simple, not discounted)
    let cumulativeCashFlow = 0;
    let paybackPeriod = 0;
    
    for (let i = 0; i < cashFlows.length; i++) {
      cumulativeCashFlow += cashFlows[i];
      if (cumulativeCashFlow >= initial) {
        const remainingAmount = initial - (cumulativeCashFlow - cashFlows[i]);
        paybackPeriod = i + (remainingAmount / cashFlows[i]);
        break;
      }
    }
    
    if (cumulativeCashFlow < initial) {
      paybackPeriod = null; // Doesn't pay back within period
    }

    let investmentDecision = "";
    let investmentRating = "";

    if (npv > 0) {
      investmentDecision = "Accept";
      if (npv > initial * 0.5) {
        investmentRating = "Excellent Investment";
      } else if (npv > initial * 0.3) {
        investmentRating = "Very Good Investment";
      } else if (npv > initial * 0.1) {
        investmentRating = "Good Investment";
      } else {
        investmentRating = "Marginal Investment";
      }
    } else if (npv === 0) {
      investmentDecision = "Neutral";
      investmentRating = "Break-even Investment";
    } else {
      investmentDecision = "Reject";
      investmentRating = "Value Destroying";
    }

    setResult({
      npv: npv.toFixed(2),
      totalCashInflows: totalCashInflows.toFixed(2),
      presentValueInflows: presentValueInflows.toFixed(2),
      irr: irrResult !== null ? irrResult.toFixed(2) : "N/A",
      profitabilityIndex: profitabilityIndex.toFixed(2),
      paybackPeriod: paybackPeriod !== null ? paybackPeriod.toFixed(2) : "Beyond 5 years",
      investmentDecision,
      investmentRating,
    });
  };

  useEffect(() => {
    calculateNPV();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <h1>NPV Calculator (Net Present Value)</h1>
          <p>
            Calculate the Net Present Value of your investment to make informed financial decisions
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Investment & Cash Flow Details
              </h2>

              {/* Initial Investment */}
              <div className="input-group">
                <label>Initial Investment (Outflow)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={initialInvestment}
                    onChange={(e) => setInitialInvestment(e.target.value)}
                    className="calc-input"
                    placeholder="500000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 2000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setInitialInvestment(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Discount Rate */}
              <div className="input-group">
                <label>Discount Rate (Required Return)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={discountRate}
                    onChange={(e) => setDiscountRate(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                    step="0.5"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[8, 10, 12, 15].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setDiscountRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Period Type */}
              <div className="input-group">
                <label>Cash Flow Period</label>
                <div className="quick-select-group">
                  {[
                    { value: "annual", label: "Annual" },
                    { value: "quarterly", label: "Quarterly" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setPeriodType(type.value)}
                      className={`quick-select-btn ${periodType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Year 1 Cash Flow */}
              <div className="input-group">
                <label>Year 1 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year1CashFlow}
                    onChange={(e) => setYear1CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="150000"
                  />
                </div>
                <div className="quick-select-group">
                  {[100000, 150000, 200000, 300000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setYear1CashFlow(amount)}
                      className="quick-select-btn success"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Year 2 Cash Flow */}
              <div className="input-group">
                <label>Year 2 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year2CashFlow}
                    onChange={(e) => setYear2CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="180000"
                  />
                </div>
              </div>

              {/* Year 3 Cash Flow */}
              <div className="input-group">
                <label>Year 3 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year3CashFlow}
                    onChange={(e) => setYear3CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="200000"
                  />
                </div>
              </div>

              {/* Year 4 Cash Flow */}
              <div className="input-group">
                <label>Year 4 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year4CashFlow}
                    onChange={(e) => setYear4CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="220000"
                  />
                </div>
              </div>

              {/* Year 5 Cash Flow */}
              <div className="input-group">
                <label>Year 5 Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year5CashFlow}
                    onChange={(e) => setYear5CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="250000"
                  />
                </div>
              </div>

              <button onClick={calculateNPV} className="calculate-btn">
                Calculate NPV
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Investment Analysis
              </h2>

              <div className="result-card">
                {/* NPV - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Net Present Value (NPV)</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.npv).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.investmentDecision} - {result.investmentRating}
                  </div>
                </div>

                {/* IRR */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Internal Rate of Return (IRR)</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                        <polyline points="17 6 23 6 23 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.irr === "N/A" ? "N/A" : `${result.irr}%`}
                  </div>
                  <div className="result-subtext">
                    {result.irr !== "N/A" && parseFloat(result.irr) > discountRate 
                      ? "Above required return" 
                      : result.irr !== "N/A" 
                        ? "Below required return" 
                        : "Cannot calculate"}
                  </div>
                </div>

                {/* Profitability Index */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Profitability Index</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.profitabilityIndex}
                  </div>
                  <div className="result-subtext">
                    {parseFloat(result.profitabilityIndex) > 1 ? "Value creating" : "Value destroying"}
                  </div>
                </div>

                {/* Payback Period */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Payback Period</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.paybackPeriod === "Beyond 5 years" ? result.paybackPeriod : `${result.paybackPeriod} years`}
                  </div>
                  <div className="result-subtext">
                    Time to recover investment
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Cash Flow Analysis</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Total Cash Inflows</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.totalCashInflows).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Present Value of Inflows</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.presentValueInflows).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.presentValueInflows) / parseFloat(result.totalCashInflows)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Initial Investment</span>
                      <span className="breakdown-percentage">
                        ₹{Number(initialInvestment).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(initialInvestment / parseFloat(result.totalCashInflows)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Decision Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: parseFloat(result.npv) > 0 ? '#f0fdf4' : '#fef2f2', borderRadius: '8px', border: `1px solid ${parseFloat(result.npv) > 0 ? '#10b981' : '#ef4444'}`}}>
                  <h3 className="breakdown-title" style={{color: parseFloat(result.npv) > 0 ? '#065f46' : '#991b1b', marginBottom: '0.5rem'}}>Investment Decision</h3>
                  <p style={{fontSize: '0.875rem', color: parseFloat(result.npv) > 0 ? '#065f46' : '#991b1b', lineHeight: '1.5', margin: 0}}>
                    {parseFloat(result.npv) > 0 
                      ? `This investment creates value of ₹${Number(result.npv).toLocaleString("en-IN")} in today's terms. The project should be accepted as NPV is positive and exceeds the required return of ${discountRate}%.`
                      : parseFloat(result.npv) < 0
                        ? `This investment destroys value of ₹${Math.abs(Number(result.npv)).toLocaleString("en-IN")}. The project should be rejected as it doesn't meet the required return of ${discountRate}%.`
                        : "This investment breaks even with zero value creation. Consider alternative opportunities."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Net Present Value (NPV)</h3>
              <p>
                Net Present Value (NPV) is a fundamental capital budgeting technique that calculates the present value 
                of all future cash flows from an investment, minus the initial investment cost. It accounts for the time 
                value of money - the principle that money today is worth more than the same amount in the future due to 
                its earning potential. NPV discounts future cash flows back to present value using a discount rate (required 
                rate of return or cost of capital). If NPV is positive, the investment generates returns exceeding the 
                required rate and creates value - accept the project. If NPV is negative, the investment destroys value - 
                reject it. Zero NPV means the investment exactly meets the required return - indifferent.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>NPV Formula:</h4>
            <div className="formula-display">
              NPV = Σ [CFt ÷ (1 + r)^t] - Initial Investment
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">CFt</div>
                <div className="formula-param-value">Cash flow at time t (year/period)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Discount rate (required return)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">t</div>
                <div className="formula-param-value">Time period (year number)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Profitability Index</div>
                <div className="formula-param-value">PV of inflows ÷ Initial investment</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How do I choose the right discount rate for NPV calculation?</h5>
              <p>
                The discount rate represents your required rate of return or opportunity cost of capital. Several approaches 
                exist for selecting it. For corporate investments, use Weighted Average Cost of Capital (WACC) - the 
                blended cost of debt and equity financing, typically 10-15% for Indian companies. For equity investments, 
                use expected market returns (10-12% for equity markets historically) plus risk premium for specific sectors. 
                For risk-free comparison, use government bond yields (6-7% in India). Higher risk projects demand higher 
                discount rates: established businesses (8-10%), new ventures (15-20%), startups or high-tech (20-30%). 
                
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between NPV and IRR?</h5>
              <p>
                NPV and IRR are complementary capital budgeting tools with key differences. NPV calculates the absolute 
                rupee value created or destroyed by an investment at a specific discount rate, giving you the actual 
                value addition (e.g., ₹2.5 lakhs). IRR calculates the discount rate at which NPV equals zero - essentially 
                the investment's rate of return. Accept projects where IRR exceeds your required return. NPV is generally 
                superior because it shows absolute value creation, handles non-conventional cash flows better (multiple 
                sign changes), and correctly ranks mutually exclusive projects. 
              </p>
            </div>

            <div className="faq-item">
              <h5>How should I estimate future cash flows accurately?</h5>
              <p>
                Accurate cash flow estimation is critical for reliable NPV analysis. Follow these guidelines: Include only 
                incremental cash flows directly resulting from the investment - what changes because of this decision. 
                Exclude sunk costs (already spent, irrelevant to future), allocated overhead (unless truly incremental), 
                and financing costs (captured in discount rate). Include opportunity costs - value of foregone alternatives. 
                Consider working capital changes - increases tie up cash (negative flow), decreases release cash (positive). 
                Use after-tax cash flows - subtract taxes on additional income. Project realistic scenarios: pessimistic, 
                most likely, and optimistic.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the limitations of NPV analysis?</h5>
              <p>
                While NPV is the gold standard for investment evaluation, it has limitations you should understand. First, 
                NPV requires accurate cash flow forecasts which are inherently uncertain - garbage in, garbage out applies. 
                Small changes in assumptions can dramatically alter NPV. Second, choosing appropriate discount rate is 
                subjective and significantly impacts results - 2% rate difference can change accept/reject decisions. Third, 
                NPV doesn't capture strategic value, competitive positioning, or real options (flexibility to expand, abandon, 
                or delay projects). Fourth, it assumes cash flows can be forecasted reliably over entire project life - 
                difficult for long-term projects (10+ years) or disruptive innovations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NPVCalculator